import { useEffect, useRef } from 'react';

import { isMuted } from '@/components/MuteButton';

/**
 * Audio manager — plays one-shot SFX and ambient drone.
 *
 * Respects the global mute flag (set via MuteButton in the header,
 * persisted in localStorage). When muted:
 *   • play() is a no-op
 *   • ambient(true) sets a flag but doesn't actually start; if user
 *     unmutes later, the next ambient(true) call will start it
 */
type Sfx = 'bell' | 'shuffle' | 'card-flip';

const SFX_SRC: Record<Sfx, string> = {
  'bell': '/audio/bell.mp3',
  'shuffle': '/audio/shuffle.mp3',
  'card-flip': '/audio/card-flip.mp3',
};

/* -------- Web Audio fallback synthesisers -------- */

let sharedCtx: AudioContext | null = null;
function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (sharedCtx) return sharedCtx;
  const C = (window.AudioContext || (window as any).webkitAudioContext);
  if (!C) return null;
  sharedCtx = new C();
  return sharedCtx;
}

function synthBell(ctx: AudioContext, vol = 0.3) {
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0, now);
  master.gain.linearRampToValueAtTime(vol, now + 0.01);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 1.6);
  master.connect(ctx.destination);
  [880, 1320].forEach((freq, i) => {
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = i === 0 ? 1 : 0.45;
    o.connect(g).connect(master);
    o.start(now);
    o.stop(now + 1.7);
  });
}

function synthShuffle(ctx: AudioContext, vol = 0.18) {
  const now = ctx.currentTime;
  const len = 0.6;
  const buf = ctx.createBuffer(1, ctx.sampleRate * len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 2500;
  bp.Q.value = 0.7;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(vol, now + 0.05);
  g.gain.exponentialRampToValueAtTime(0.0001, now + len);
  src.connect(bp).connect(g).connect(ctx.destination);
  src.start(now);
}

function synthFlip(ctx: AudioContext, vol = 0.2) {
  const now = ctx.currentTime;
  const o = ctx.createOscillator();
  o.type = 'sawtooth';
  o.frequency.setValueAtTime(900, now);
  o.frequency.exponentialRampToValueAtTime(120, now + 0.18);
  const g = ctx.createGain();
  g.gain.setValueAtTime(vol, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2000;
  o.connect(lp).connect(g).connect(ctx.destination);
  o.start(now);
  o.stop(now + 0.22);
}

function startAmbientSynth(ctx: AudioContext, vol = 0.06) {
  const master = ctx.createGain();
  master.gain.value = vol;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 800;
  master.connect(lp).connect(ctx.destination);
  const oscs: OscillatorNode[] = [];
  [65.41, 98.0, 164.81].forEach((freq, i) => {
    const o = ctx.createOscillator();
    o.type = i === 2 ? 'triangle' : 'sine';
    o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = 0.5;
    o.connect(g).connect(master);
    o.start();
    oscs.push(o);

    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07 + i * 0.04;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = freq * 0.005;
    lfo.connect(lfoGain).connect(o.frequency);
    lfo.start();
  });
  return () => oscs.forEach((o) => o.stop());
}

/* -------- React hook -------- */

export function useAudio() {
  const sfxCache = useRef<Partial<Record<Sfx, HTMLAudioElement>>>({});
  const ambientFile = useRef<HTMLAudioElement | null>(null);
  const stopAmbientSynth = useRef<(() => void) | null>(null);
  const ambientWanted = useRef<boolean>(false);

  // React to mute toggling from the header.
  useEffect(() => {
    const onMute = (e: Event) => {
      const muted = (e as CustomEvent).detail as boolean;
      if (muted) {
        ambientFile.current?.pause();
        stopAmbientSynth.current?.();
        stopAmbientSynth.current = null;
      } else if (ambientWanted.current) {
        // restart whatever ambient layer was wanted
        _startAmbient();
      }
    };
    window.addEventListener('mc:muted-changed', onMute);
    return () => window.removeEventListener('mc:muted-changed', onMute);
  }, []);

  useEffect(() => {
    return () => {
      ambientFile.current?.pause();
      stopAmbientSynth.current?.();
    };
  }, []);

  const _startAmbient = () => {
    if (!ambientFile.current) {
      ambientFile.current = new Audio('/audio/ambient.mp3');
      ambientFile.current.loop = true;
      ambientFile.current.volume = 0.15;
    }
    ambientFile.current.play().catch(() => {
      const ctx = getCtx();
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume();
      stopAmbientSynth.current?.();
      stopAmbientSynth.current = startAmbientSynth(ctx);
    });
  };

  const play = (sound: Sfx) => {
    if (isMuted()) return;
    let el = sfxCache.current[sound];
    if (!el) {
      el = new Audio(SFX_SRC[sound]);
      el.volume = 0.4;
      sfxCache.current[sound] = el;
    }
    el.currentTime = 0;
    el.play().catch(() => {
      const ctx = getCtx();
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume();
      if (sound === 'bell') synthBell(ctx);
      else if (sound === 'shuffle') synthShuffle(ctx);
      else if (sound === 'card-flip') synthFlip(ctx);
    });
  };

  const ambient = (enabled: boolean) => {
    ambientWanted.current = enabled;
    if (!enabled || isMuted()) {
      ambientFile.current?.pause();
      stopAmbientSynth.current?.();
      stopAmbientSynth.current = null;
      return;
    }
    _startAmbient();
  };

  return { play, ambient };
}
